import { ArrowDownward, ArrowUpward, CloudDownload } from '@mui/icons-material';
import { Button, TextField, Typography } from '@mui/material';
import { Box, Grid } from '@mui/system';

import { exportCompanies } from 'api';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateButton } from 'app/components';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { translations } from 'locales/i18n';

// Define sort types
export type SortDirection = 'asc' | 'desc' | null;
export type SortField = 'name' | 'createdAt' | null;

interface Props {
  search: string;
  setSearch: (search: string) => void;
  filters: {
    active: boolean;
    inactive: boolean;
    banned: boolean;
  };
  setFilters: Dispatch<
    SetStateAction<{
      active: boolean;
      inactive: boolean;
      banned: boolean;
    }>
  >;
  handleCreateClick: () => void;
  sortField: SortField;
  setSortField: Dispatch<SetStateAction<SortField>>;
  sortDirection: SortDirection;
  setSortDirection: Dispatch<SetStateAction<SortDirection>>;
}

export const FilterHeader = ({
  filters,
  setFilters,
  handleCreateClick,
  search,
  setSearch,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection
}: Props) => {
  const { t } = useTranslation();
  const toggleFilter = (filter: 'active' | 'inactive' | 'banned') => {
    setFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  };

  // Sort state
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      // Set new field and direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper to render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;

    if (sortDirection === 'asc') {
      return <ArrowUpward sx={{ fontSize: 18, ml: 0.5 }} />;
    } else if (sortDirection === 'desc') {
      return <ArrowDownward sx={{ fontSize: 18, ml: 0.5 }} />;
    }

    return null;
  };

  return (
    <Grid container justifyContent={'space-between'} gap={2} marginBottom={2}>
      <Grid container gap={2}>
        <TextField
          size="small"
          placeholder={t(translations.pages.manageCompanies.companyName())}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            variant={filters.active ? 'contained' : 'outlined'}
            onClick={() => toggleFilter('active')}
            sx={{
              borderRadius: 4,
              bgcolor: filters.active ? '#a9fcd6' : 'transparent',
              color: filters.active ? '#166534' : 'inherit',
              borderColor: filters.active ? '#86efac' : 'inherit',
              '&:hover': {
                bgcolor: filters.active ? '#bbf7d0' : '#f0fdf4'
              },
              textTransform: 'none'
            }}
          >
            {t(translations.pages.manageCompanies.activeCompanies())}
          </Button>
          <Button
            variant={filters.inactive ? 'contained' : 'outlined'}
            onClick={() => toggleFilter('inactive')}
            sx={{
              borderRadius: 4,
              bgcolor: filters.inactive ? '#e5e7eb' : 'transparent',
              color: filters.inactive ? '#1f2937' : 'inherit',
              borderColor: filters.inactive ? '#d1d5db' : 'inherit',
              '&:hover': {
                bgcolor: filters.inactive ? '#d1d5db' : '#f9fafb'
              },
              textTransform: 'none'
            }}
          >
            {t(translations.pages.manageCompanies.inactiveCompanies())}
          </Button>
          <Button
            variant={filters.banned ? 'contained' : 'outlined'}
            onClick={() => toggleFilter('banned')}
            sx={{
              borderRadius: 4,
              bgcolor: filters.banned ? '#fee2e2' : 'transparent',
              color: filters.banned ? '#991b1b' : 'inherit',
              borderColor: filters.banned ? '#fecaca' : 'inherit',
              '&:hover': {
                bgcolor: filters.banned ? '#fecaca' : '#fef2f2'
              },
              textTransform: 'none'
            }}
          >
            {t(translations.pages.manageCompanies.bannedCompanies())}
          </Button>
        </Box>
      </Grid>
      <Grid container gap={1} alignItems={'center'}>
        <Button
          startIcon={<CloudDownload />}
          variant="contained"
          className="manage-companies__actions--download"
          onClick={() => handleDownload('companies.xlsx', exportCompanies())}
        >
          {t(translations.common.export())}
        </Button>
        <CreateButton onClick={handleCreateClick}>{t(translations.common.create())}</CreateButton>
      </Grid>
      {/* Table Header */}
      <Grid container size={12} sx={{ display: 'flex' }}>
        <Box
          onClick={() => handleSort('name')}
          sx={{
            flex: 1,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            justifyContent: 'center',
            border: 1,
            borderColor: 'lightgray',
            backgroundColor: sortField === 'name' ? 'orange' : undefined,
            borderRadius: '8px 0 0 8px'
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {t(translations.pages.manageCompanies.companyName())}
            {renderSortIcon('name')}
          </Typography>
        </Box>
        <Box
          onClick={() => handleSort('createdAt')}
          sx={{
            flex: 1,
            p: 1.5,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            borderColor: 'lightgray',
            backgroundColor: sortField === 'createdAt' ? 'orange' : undefined,
            borderRadius: '0 8px 8px 0'
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {t(translations.pages.manageCompanies.registerDate())}
            {renderSortIcon('createdAt')}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};
